// Notification Service for Chess Tournament PWA
// Handles push notifications, in-app notifications, and notification permissions

class NotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.permissionGranted = false;
    this.initializeService();
  }

  async initializeService() {
    try {
      // Check if service worker is supported
      if ('serviceWorker' in navigator) {
        this.registration = await navigator.serviceWorker.ready;
      }

      // Check current permission status
      this.permissionGranted = Notification.permission === 'granted';

      // Subscribe to push notifications if permission granted
      if (this.permissionGranted && 'PushManager' in window) {
        await this.subscribeToPush();
      }

      console.log('✅ Notification Service initialized');
    } catch (error) {
      console.error('❌ Failed to initialize notification service:', error);
    }
  }

  // Request notification permission from user
  async requestPermission() {
    try {
      if (!('Notification' in window)) {
        throw new Error('This browser does not support notifications');
      }

      const permission = await Notification.requestPermission();
      this.permissionGranted = permission === 'granted';

      if (this.permissionGranted) {
        console.log('✅ Notification permission granted');
        await this.subscribeToPush();
        return true;
      } else {
        console.log('❌ Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return false;
    }
  }

  // Subscribe to push notifications
  async subscribeToPush() {
    try {
      if (!this.registration || !('PushManager' in window)) {
        console.log('Push messaging not supported');
        return null;
      }

      // Get VAPID key first so we can compare with existing subscription
      const vapidPublicKey = await this.getVapidPublicKey();
      if (!vapidPublicKey) {
        console.log('No VAPID key available');
        return null;
      }

      // Check if already subscribed
      let existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        this.subscription = existingSubscription;
        // Always re-send to server to ensure DB has fresh/valid entry
        await this.sendSubscriptionToServer(this.subscription);
        console.log('✅ Push subscription refreshed on server');
        return existingSubscription;
      }

      // Create new subscription
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      await this.sendSubscriptionToServer(this.subscription);
      console.log('✅ Push notification subscription created');

      return this.subscription;
    } catch (error) {
      console.error('❌ Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  // Get VAPID public key from server
  async getVapidPublicKey() {
    try {
      const response = await fetch('/api/notifications/vapid-key');
      if (!response.ok) return null;

      const data = await response.json();
      return data.publicKey;
    } catch (error) {
      console.error('Failed to get VAPID key:', error);
      return null;
    }
  }

  // Send subscription to server for storage
  async sendSubscriptionToServer(subscription) {
    try {
      // Use toJSON() to ensure keys (p256dh, auth) are included —
      // Chrome hides them behind non-enumerable getters on the raw object
      const subscriptionJson = subscription.toJSON ? subscription.toJSON() : subscription;

      const response = await fetch('/api/notifications/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscriptionJson,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription on server');
      }

      console.log('✅ Subscription saved to server');
    } catch (error) {
      console.error('❌ Failed to save subscription:', error);
      throw error;
    }
  }

  // Show local notification (when app is open)
  showLocalNotification(title, options = {}) {
    if (!this.permissionGranted) {
      console.log('No notification permission');
      return;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/favicon.ico',
      tag: 'chess-tournament',
      requireInteraction: false,
      silent: false
    };

    const notification = new Notification(title, { ...defaultOptions, ...options });

    // Auto-close after 5 seconds if not requiring interaction
    if (!options.requireInteraction) {
      setTimeout(() => notification.close(), 5000);
    }

    return notification;
  }

  // Tournament-specific notification methods
  notifyRoundStarting(tournamentName, round, timeMinutes = 5) {
    return this.showLocalNotification(
      `Round ${round} Starting Soon!`,
      {
        body: `${tournamentName} - Round ${round} starts in ${timeMinutes} minutes`,
        icon: '/logo192.png',
        tag: `round-starting-${round}`,
        actions: [
          {
            action: 'view-tournament',
            title: 'View Tournament'
          },
          {
            action: 'dismiss',
            title: 'Dismiss'
          }
        ]
      }
    );
  }

  notifyPairingReady(tournamentName, round) {
    return this.showLocalNotification(
      `Your Pairing is Ready!`,
      {
        body: `${tournamentName} - Round ${round} pairings have been posted`,
        icon: '/logo192.png',
        tag: `pairing-ready-${round}`,
        requireInteraction: true,
        actions: [
          {
            action: 'view-pairing',
            title: 'View Pairing'
          }
        ]
      }
    );
  }

  notifyResultsPosted(tournamentName, round) {
    return this.showLocalNotification(
      `Results Posted`,
      {
        body: `${tournamentName} - Round ${round} results are now available`,
        icon: '/logo192.png',
        tag: `results-posted-${round}`,
        actions: [
          {
            action: 'view-results',
            title: 'View Results'
          }
        ]
      }
    );
  }

  notifyTournamentComplete(tournamentName) {
    return this.showLocalNotification(
      `Tournament Complete!`,
      {
        body: `${tournamentName} has finished. Check final standings!`,
        icon: '/logo192.png',
        tag: 'tournament-complete',
        requireInteraction: true,
        actions: [
          {
            action: 'view-standings',
            title: 'View Final Standings'
          }
        ]
      }
    );
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    try {
      if (this.subscription) {
        await this.subscription.unsubscribe();

        // Notify server to remove subscription
        await fetch('/api/notifications/unsubscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: this.subscription.endpoint
          })
        });

        this.subscription = null;
        console.log('✅ Unsubscribed from push notifications');
        return true;
      }
    } catch (error) {
      console.error('❌ Error unsubscribing:', error);
      return false;
    }
  }

  // Update app badge (notification count)
  async updateBadge(count = 0) {
    try {
      if ('setAppBadge' in navigator) {
        if (count > 0) {
          await navigator.setAppBadge(count);
        } else {
          await navigator.clearAppBadge();
        }
      }
    } catch (error) {
      console.error('Failed to update app badge:', error);
    }
  }

  // Subscribe to player-specific notifications for a tournament
  async subscribeToPlayerNotifications(tournamentId, playerId, playerName) {
    try {
      // Always refresh push subscription to ensure DB has latest valid entry
      const pushSub = await this.subscribeToPush();
      if (!pushSub) {
        throw new Error('Failed to set up push notifications. Please ensure notifications are allowed in your browser.');
      }

      // Use toJSON() to properly serialize the subscription including keys
      const subscriptionJson = this.subscription.toJSON
        ? this.subscription.toJSON()
        : this.subscription;

      const response = await fetch(`/api/tournaments/${tournamentId}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId,
          playerName,
          subscription: subscriptionJson
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to subscribe to player notifications');
      }

      const result = await response.json();
      console.log('✅ Subscribed to notifications for player:', playerName);
      return result;
    } catch (error) {
      console.error('❌ Failed to subscribe to player notifications:', error);
      throw error;
    }
  }

  // Unsubscribe from player-specific notifications for a tournament
  async unsubscribeFromPlayerNotifications(tournamentId, playerId, playerName) {
    try {
      const response = await fetch(`/api/tournaments/${tournamentId}/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId,
          playerName,
          subscription: this.subscription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe from player notifications');
      }

      const result = await response.json();
      console.log('✅ Unsubscribed from notifications for player:', playerName);
      return result;
    } catch (error) {
      console.error('❌ Failed to unsubscribe from player notifications:', error);
      throw error;
    }
  }

  // Check if subscribed to a specific player's notifications
  async isSubscribedToPlayer(tournamentId, playerId, playerName) {
    try {
      const response = await fetch(`/api/tournaments/${tournamentId}/notifications/status?playerId=${playerId}&playerName=${encodeURIComponent(playerName)}`);

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result.subscribed || false;
    } catch (error) {
      console.error('❌ Failed to check player subscription status:', error);
      return false;
    }
  }

  // Subscribe to tournament TD notifications
  async subscribeToTournamentNotifications(tournamentId, role = 'TD') {
    try {
      if (!this.subscription) {
        const success = await this.subscribeToPush();
        if (!success) {
          throw new Error('Failed to set up push notifications');
        }
      }

      const response = await fetch(`/api/mobile/td-notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournamentId,
          role,
          subscription: this.subscription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe to TD notifications');
      }

      const result = await response.json();
      console.log('✅ Subscribed to TD notifications for tournament:', tournamentId);
      return result;
    } catch (error) {
      console.error('❌ Failed to subscribe to TD notifications:', error);
      throw error;
    }
  }

  // Unsubscribe from tournament TD notifications
  async unsubscribeFromTournamentNotifications(tournamentId) {
    try {
      const response = await fetch(`/api/mobile/td-notifications/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournamentId,
          subscription: this.subscription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe from TD notifications');
      }

      const result = await response.json();
      console.log('✅ Unsubscribed from TD notifications for tournament:', tournamentId);
      return result;
    } catch (error) {
      console.error('❌ Failed to unsubscribe from TD notifications:', error);
      throw error;
    }
  }

  // Check if subscribed to tournament TD notifications
  async isSubscribedToTournament(tournamentId) {
    try {
      const response = await fetch(`/api/mobile/td-notifications/status?tournamentId=${tournamentId}`);

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result.subscribed || false;
    } catch (error) {
      console.error('❌ Failed to check TD subscription status:', error);
      return false;
    }
  }

  // Check if notifications are supported and enabled
  get isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  get isEnabled() {
    return this.permissionGranted;
  }

  get permissionStatus() {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  }

  // Utility function to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;